import { useEffect, useRef } from "react";
import { CheckCircle2, X } from "lucide-react";
import { billingLine, inr } from "@/lib/plans";

const PaymentButton = ({ buttonId, kind }) => {
  const ref = useRef(null);
  useEffect(() => {
    const host = ref.current;
    const form = document.createElement("form");
    const script = document.createElement("script");
    script.async = true;
    if (kind === "payment") {
      script.src = "https://checkout.razorpay.com/v1/payment-button.js";
      script.dataset.payment_button_id = buttonId;
    } else {
      script.src = "https://cdn.razorpay.com/static/widget/subscription-button.js";
      script.dataset.subscription_button_id = buttonId;
      script.dataset.button_theme = "brand-color";
    }
    form.appendChild(script);
    host.appendChild(form);
    return () => {
      host.innerHTML = "";
      document.querySelectorAll(".razorpay-payment-form-container, iframe[src*='subscription_buttons'], iframe[src*='payment-button']").forEach((f) => f.remove());
    };
  }, [buttonId, kind]);
  return <div ref={ref} className="flex justify-center min-h-[48px]" data-testid="subscribe-razorpay-button" />;
};

const razorpayFormOpen = () => {
  const el = document.querySelector(".razorpay-payment-form-container, .razorpay-container");
  return !!el && el.offsetParent !== null && getComputedStyle(el).display !== "none";
};

const Reminder = ({ plan }) => (
  <div className="mt-6 mb-8 mx-auto max-w-md rounded-2xl border-2 border-amber-400 bg-amber-50 px-5 py-4 text-left" role="note" data-testid="subscribe-plan-reminder">
    <p className="text-xs uppercase tracking-[0.25em] text-amber-800 font-semibold">Important</p>
    <p className="text-stone-900 mt-1">{plan.recurring ? "In Razorpay, please choose the same plan you picked here:" : "You're paying once for a single envelope:"}</p>
    <p className="font-serif-display text-2xl text-stone-900 mt-1" data-testid="subscribe-plan-reminder-plan">{plan.recurring ? plan.rzpName : plan.name} · {inr(plan.price)}</p>
    <p className="text-sm text-stone-600">{plan.recurring ? `${plan.name} · ` : ""}{billingLine(plan)}</p>
  </div>
);

export const ButtonPayOverlay = ({ buttonId, kind, plan, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !razorpayFormOpen() && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 bg-stone-900/60 overflow-y-auto p-4 flex" onClick={(e) => e.target === e.currentTarget && !razorpayFormOpen() && onClose()} data-testid="subscribe-button-overlay">
      <div role="dialog" aria-modal="true" aria-label="Complete your subscription" className="relative m-auto w-full max-w-xl bg-[#FFFDF9] rounded-lg shadow-lg text-center py-12 px-6" data-testid="subscribe-ready">
        <button onClick={onClose} className="absolute right-4 top-4 text-stone-500 hover:text-stone-900" aria-label="Close" data-testid="subscribe-ready-close"><X className="h-5 w-5" /></button>
        <CheckCircle2 className="h-14 w-14 text-amber-600 mx-auto" strokeWidth={1.5} />
        <p className="font-serif-display text-3xl mt-5">Your address is saved!</p>
        <p className="text-stone-600 mt-3">One last step: complete your {plan.recurring ? "subscription" : "payment"} on Razorpay. Please use the same email and phone.</p>
        <Reminder plan={plan} />
        <PaymentButton buttonId={buttonId} kind={kind} />
      </div>
    </div>
  );
};
